import React, { useEffect, useState } from "react";
import Tooltip from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { ToastContainer, toast } from "react-toastify";
import ContentLoader from "react-content-loader";
import { Link, useNavigate } from "react-router-dom";

import { getTrans as t } from "../../Localization/translations";
import ButtonLoading from "../../components/Loder/buttonLoading";
import TaxEngineService from "../../services/company/taxEngine.service";

const toastOptions = {
  position: "top-right",
  autoClose: 2000,
  hideProgressBar: false,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light",
};

const errorMessage = (error) =>
  error?.response?.data?.error || error?.response?.data?.detail || "Something went wrong.";

// One field (same label / tooltip / input markup as the ACH Provider form).
// Read-only unless an onChange is given.
const Field = ({ label, tooltip, value, onChange, type = "text", placeholder }) => (
  <div className="col-md-6">
    <label className="col-form-label">
      {label}&nbsp;
      {tooltip && (
        <Tooltip content={tooltip}>
          <i className="fa fa-light fa-circle-info"></i>
        </Tooltip>
      )}
    </label>
    <input
      className="form-control"
      type={type}
      value={value ?? ""}
      placeholder={placeholder}
      onChange={onChange ? (e) => onChange(e.target.value) : undefined}
      readOnly={!onChange}
      disabled={!onChange}
    />
  </div>
);

// Result line under the buttons: green when it worked, red with Avalara's message when not.
const Result = ({ result }) => {
  if (!result) return null;
  return result.success ? (
    <div className="text-success mt-2">{result.message}</div>
  ) : (
    <div className="text-danger mt-2">{result.error}</div>
  );
};

const TaxEngine = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [testing, setTesting] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  const [validating, setValidating] = useState(false);
  const [addressResult, setAddressResult] = useState(null);

  // Editable copies of the saved values. They are only used to try the connection /
  // validate an address; nothing typed here is saved.
  const [account, setAccount] = useState({ number: "", licenseKey: "", serviceUrl: "", companyCode: "" });
  const [accountTouched, setAccountTouched] = useState({});
  const [address, setAddress] = useState({ address: "", city: "", state: "", zip_code: "", country: "" });

  useEffect(() => {
    TaxEngineService.getTaxEngine()
      .then((response) => {
        const data = response.data;
        setInfo(data);
        setAccount({
          number: data.account_number,
          licenseKey: "",
          serviceUrl: data.service_url,
          companyCode: data.company_code,
        });
        setAddress({
          address: data.origin_address.address || "",
          city: data.origin_address.city || "",
          state: data.origin_address.state || "",
          zip_code: data.origin_address.zip_code ?? "",
          country: data.origin_address.country || "",
        });
      })
      .catch((error) => setLoadError(errorMessage(error)))
      .finally(() => setLoading(false));
  }, []);

  const changeAccount = (key) => (value) => {
    setAccount((prev) => ({ ...prev, [key]: value }));
    setAccountTouched((prev) => ({ ...prev, [key]: true }));
  };
  const changeAddress = (key) => (value) => setAddress((prev) => ({ ...prev, [key]: value }));

  const handleTestConnection = () => {
    setTesting(true);
    setConnectionResult(null);
    // Only what the user actually changed is sent; anything left alone uses the saved value.
    TaxEngineService.testConnection({
      ...(accountTouched.number ? { account_number: account.number } : {}),
      ...(account.licenseKey ? { license_key: account.licenseKey } : {}),
      ...(accountTouched.serviceUrl ? { service_url: account.serviceUrl } : {}),
      ...(accountTouched.companyCode ? { company_code: account.companyCode } : {}),
    })
      .then((response) => {
        setConnectionResult(response.data);
        if (response.data.success) {
          toast.success(response.data.message, toastOptions);
        } else {
          toast.error(response.data.error, toastOptions);
        }
      })
      .catch((error) => {
        const message = errorMessage(error);
        setConnectionResult({ success: 0, error: message });
        toast.error(message, toastOptions);
      })
      .finally(() => setTesting(false));
  };

  const handleValidateAddress = () => {
    setValidating(true);
    setAddressResult(null);
    TaxEngineService.validateOriginAddress(address)
      .then((response) => {
        setAddressResult(response.data);
        if (response.data.success) {
          toast.success(response.data.message, toastOptions);
        } else {
          toast.error(response.data.error, toastOptions);
        }
      })
      .catch((error) => {
        const message = errorMessage(error);
        setAddressResult({ success: 0, error: message });
        toast.error(message, toastOptions);
      })
      .finally(() => setValidating(false));
  };

  const adminConsoleUrl =
    info && info.environment === "production"
      ? "https://admin.avalara.com"
      : "https://sandbox.admin.avalara.com";

  return (
    <div className="page-wrapper">
      <ToastContainer />
      <div className="content container-fluid">
        <div className="header pb-0 mb-3 pt-3">
          <h5>{t("Tax Engine")}</h5>
          <h6>
            <Link to="/settings" className="tc">
              {t("Settings")}
            </Link>{" "}
            / <span>{t("Tax Engine")}</span>
          </h6>
          <div className="show_invoice">
            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-body">
                    {loading ? (
                      <ContentLoader
                        viewBox="0 0 400 160"
                        backgroundColor="rgba(0,0,0,0.06)"
                        foregroundColor="rgba(0,0,0,0.12)"
                      >
                        <rect x="0" y="13" rx="4" ry="4" width="400" height="9" />
                        <rect x="0" y="29" rx="4" ry="4" width="100" height="8" />
                        <rect x="0" y="50" rx="4" ry="4" width="400" height="10" />
                        <rect x="0" y="65" rx="4" ry="4" width="400" height="10" />
                        <rect x="0" y="99" rx="5" ry="5" width="400" height="60" />
                      </ContentLoader>
                    ) : loadError ? (
                      <div className="alert alert-danger">{loadError}</div>
                    ) : (
                      <>
                        <div className="row">
                          <div className="col-lg-12">
                            <div className="form-group row">
                              <Field
                                label={t("Tax Engine")}
                                tooltip="The tax provider used when Tax Calculation is set to Avalara."
                                value={info.provider}
                              />
                              <Field
                                label={t("Environment")}
                                tooltip="Sandbox is for testing; Production is live tax reporting."
                                value={info.environment}
                              />
                            </div>
                            <div className="form-group row">
                              <Field
                                label={t("Service URL")}
                                tooltip="The Avalara server every request is sent to (HTTPS only)."
                                value={account.serviceUrl}
                                onChange={changeAccount("serviceUrl")}
                              />
                              <Field
                                label={t("Company Code")}
                                tooltip="The Avalara company that transactions are recorded under."
                                value={account.companyCode}
                                onChange={changeAccount("companyCode")}
                              />
                            </div>
                            <div className="form-group row">
                              <Field
                                label={t("Account Number")}
                                tooltip="Your Avalara account number (partly hidden). Type a new one to test with it."
                                value={account.number}
                                onChange={changeAccount("number")}
                              />
                              <Field
                                label={t("License Key")}
                                tooltip="The saved license key is kept on the server and never shown. Type a key to test with it instead."
                                type="password"
                                placeholder={info.license_key_configured ? "Configured (type to test another key)" : "Not configured"}
                                value={account.licenseKey}
                                onChange={changeAccount("licenseKey")}
                              />
                            </div>
                            <div className="form-group row">
                              <Field
                                label={t("Is Active")}
                                tooltip="Whether Avalara is the selected tax calculation method. Change it in Global Setting."
                                value={info.selected_engine === "avalara" ? "Yes" : "No (Internal is selected)"}
                              />
                              <Field
                                label={t("Save and Commit Documents")}
                                tooltip="Draft invoices are saved to Avalara as uncommitted. They are committed when the invoice is Completed."
                                value="On Complete"
                              />
                            </div>

                            <div className="text-left py-2">
                              <button
                                type="button"
                                onClick={handleTestConnection}
                                className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-md"
                                disabled={testing}
                              >
                                {testing ? <ButtonLoading /> : t("Test Connection")}
                              </button>
                              <Result result={connectionResult} />
                              {connectionResult && connectionResult.success === 1 && (
                                <div className="small text-muted mt-1">
                                  {`Account ${connectionResult.details.account_id} · Company ${connectionResult.details.company_code} (id ${connectionResult.details.company_id})`}
                                </div>
                              )}
                            </div>

                            <hr className="mt-5 mb-4" />

                            <h6 className="mb-0">{t("Address validation")}</h6>
                            <div className="form-group row">
                              <Field
                                label={t("Street Address")}
                                value={address.address}
                                onChange={changeAddress("address")}
                              />
                              <Field label={t("City")} value={address.city} onChange={changeAddress("city")} />
                            </div>
                            <div className="form-group row">
                              <Field label={t("State")} value={address.state} onChange={changeAddress("state")} />
                              <Field
                                label={t("Zip Code")}
                                value={address.zip_code}
                                onChange={changeAddress("zip_code")}
                              />
                            </div>
                            <div className="form-group row">
                              <Field
                                label={t("Country")}
                                value={address.country}
                                onChange={changeAddress("country")}
                              />
                            </div>

                            <div className="text-left py-2">
                              <button
                                type="button"
                                onClick={handleValidateAddress}
                                className="btn-continue border-0 btn btn-primary btn-gradient-primary btn-md"
                                disabled={validating}
                              >
                                {validating ? <ButtonLoading /> : t("Validate Address")}
                              </button>
                              <Result result={addressResult} />
                            </div>
                          </div>
                        </div>

                        <div className="text-left py-3">
                          <button
                            type="button"
                            onClick={() => navigate("/settings")}
                            className="btn-cancel-invoice border-0 btn btn-secondary btn-gradient-primary btn-md"
                          >
                            {t("Cancel")}
                          </button>
                          &nbsp;&nbsp;
                          <a href={adminConsoleUrl} target="_blank" rel="noopener noreferrer">
                            Go to AvaTax Admin Console
                          </a>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxEngine;
